import {Injectable, inject} from '@angular/core';
import {MatIconRegistry} from '@angular/material/icon';
import {DomSanitizer} from '@angular/platform-browser';

/**
 * The app's own icons, registered once for the whole app.
 *
 * Material's icon font covers the generic verbs -- save, close, settings -- but
 * it has nothing for the things this app is actually about: a polygon's
 * vertices, an intermediate distance, a shape with a hole in it. Those were
 * being approximated by whichever ligature was closest, which is how a
 * "description label" and a "polygon label" end up wearing the same icon and
 * telling the user nothing.
 *
 * Registered centrally rather than per component so that `svgIcon="mmo-spots"`
 * works anywhere without each component knowing where the file lives. Adding
 * one means dropping an SVG in assets/icons/map and naming it below.
 *
 * The files use `currentColor` and no hard-coded size, so they inherit colour
 * and scale from whatever `mat-icon` styling surrounds them -- which is what
 * makes them work in both themes without a second set.
 */
@Injectable({providedIn: 'root'})
export class AppIconsService {

  private readonly registry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);

  /**
   * Everything under assets/icons/map, by the name it is used with.
   *
   * Prefixed so they never collide with a Material ligature: `mmo-spots` is
   * unmistakably ours, `spots` would be a guess.
   */
  private static readonly MAP_ICONS = [
    'polygons',
    'polygon-labels',
    'intermediate-distances',
    'description-labels',
    'spot-labels',
    'hole-labels',
    'spots',
    'pins',
    'comments',
    // The top toolbar's Geometries button and the crosshair modes. They were
    // PNGs set as CSS `content`, so they could not take the theme's colour
    // or line up with the mat-icons beside them.
    'geometries',
    'polygon',
    'polyline',
  ];

  /**
   * The export formats, under their own prefix.
   *
   * Separate from the map icons because they answer a different question --
   * "what will I get" rather than "what is on the map" -- and because a format
   * icon that tries to look like a page is nine identical icons at menu size.
   * Each of these shows what the format IS: a globe for Google Earth, a route
   * for GPX, a dimensioned drawing for DXF.
   */
  private static readonly FORMAT_ICONS = [
    'pdf', 'png', 'mmp', 'kml', 'geojson', 'gpx', 'dxf', 'csv', 'txt',
  ];

  register() {
    for (const name of AppIconsService.MAP_ICONS) {
      this.registry.addSvgIcon(
        `mmo-${name}`,
        this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/map/${name}.svg`),
      );
    }
    for (const name of AppIconsService.FORMAT_ICONS) {
      this.registry.addSvgIcon(
        `mmf-${name}`,
        this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/formats/${name}.svg`),
      );
    }
  }
}

/** Registered at start-up, so the first panel to open already has them. */
export function appIconsFactory(icons: AppIconsService) {
  return () => icons.register();
}
